import { useState } from 'react'
import { api } from '../services/api'
import type { RenderLabelResponse } from '@alpha-edge/shared'
import { useDymoSdk } from './use-dymo-sdk'
import { useZebraBrowserPrint } from './use-zebra-browser-print'

interface PrintArgs {
  readonly orderId: string
  readonly printerId?: string
  readonly templateId?: string
}

interface UsePrintLabelResult {
  readonly print: (args: PrintArgs) => Promise<void>
  readonly loading: boolean
  readonly error: string | null
}

export function usePrintLabel(): UsePrintLabelResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dymo = useDymoSdk()
  const zebra = useZebraBrowserPrint()

  async function print(args: PrintArgs): Promise<void> {
    setLoading(true)
    setError(null)
    try {
      const res = await api.post<RenderLabelResponse>('/labels/render', {
        orderId: args.orderId,
        ...(args.printerId ? { printerId: args.printerId } : {}),
        ...(args.templateId ? { templateId: args.templateId } : {}),
      })
      const renderData = res.data
      if (!renderData) throw new Error('No render data returned from server')

      const { driver, body, printerConnectionHint } = renderData

      if (driver === 'dymo_client') {
        await dymo.print(body, printerConnectionHint ?? '')
      } else if (driver === 'zebra_browser_print') {
        await zebra.print(body, printerConnectionHint ?? '')
      } else {
        throw new Error(`Unknown printer driver: ${String(driver)}`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Print failed'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { print, loading, error }
}
